import React from 'react';
import { atom, useAtom, useSetAtom } from 'jotai';
import { abortableAtom, atomFamily, loadable } from 'jotai/utils';
import { useActiveProjectId, useServiceAuth } from '../../hooks';
import { ServiceAuth } from '../../hooks/useService/useService';
import FhirSearchService from '../../services/FhirSearchService';
import { DOCUMENT_EXTRACTION_V2_PROJECTION_IDENTIFIER_SYSTEM } from '../../services/MedicalResultsService';
import {
  DOCUMENT_EXTRACTION_V2_APPROVED_AT_EXTENSION_URL,
  DOCUMENT_EXTRACTION_V2_APPROVED_BY_EXTENSION_URL,
  DOCUMENT_EXTRACTION_V2_REVIEW_STATUS_EXTENSION_URL,
} from '../SubjectViewer/Modules/Measurements/Graphs/Ranges/ExtractionFeedbackActions';
import { countResourcesToReview } from './memberExtractionResourceGroups';
import {
  buildMemberExtractionProvenanceQuery,
  buildMemberExtractionResourceQuery,
  ExtractedClinicalResource,
  ExtractionProvenance,
  getExtractedResourceReviewDecision,
  groupExtractionsBySource,
} from './useMemberExtractionDocuments';

const fhirSearchService = new FhirSearchService();

type MaybeSource<T> = T | (() => T);

const resolveSource = <T>(input: MaybeSource<T>): T =>
  typeof input === 'function' ? (input as () => T)() : input;

type AsyncOptions = {
  immediate?: boolean;
};

export type MemberExtractionReviewCounts = {
  // Source documents with at least one result still waiting for review.
  documentsToReview: number;
};

export type MemberExtractionReviewCountsResult = {
  data: MemberExtractionReviewCounts | undefined;
  error: unknown;
  isFetching: boolean;
  isIdle: boolean;
  execute: () => void;
};

type CountState =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'hasError'; error: unknown }
  | { state: 'hasData'; data: MemberExtractionReviewCounts | undefined };

type SearchResponse = {
  hits?: { hits?: Array<{ _source?: unknown }> };
};

const hitsOf = <T>(response: { data?: SearchResponse } | undefined): T[] =>
  (response?.data?.hits?.hits ?? [])
    .map(hit => hit._source as T | undefined)
    .filter((source): source is T => source != null);

// Clinical indices store `extension` as an object, not a nested type. A nested
// query against that path is rejected by Elasticsearch.
const approvedExtractionQuery = {
  bool: {
    should: [
      {
        bool: {
          must: [
            {
              term: {
                'extension.url.keyword':
                  DOCUMENT_EXTRACTION_V2_REVIEW_STATUS_EXTENSION_URL,
              },
            },
            {
              term: {
                'extension.valueString.keyword': 'approved',
              },
            },
          ],
        },
      },
      {
        term: {
          'extension.url.keyword':
            DOCUMENT_EXTRACTION_V2_APPROVED_BY_EXTENSION_URL,
        },
      },
      {
        term: {
          'extension.url.keyword':
            DOCUMENT_EXTRACTION_V2_APPROVED_AT_EXTENSION_URL,
        },
      },
    ],
    minimum_should_match: 1,
  },
};

export const buildMemberExtractionReviewCountQuery = () => ({
  ...buildMemberExtractionResourceQuery(),
  where: {
    type: 'elasticsearch',
    query: {
      bool: {
        filter: [
          {
            term: {
              'identifier.system.keyword':
                DOCUMENT_EXTRACTION_V2_PROJECTION_IDENTIFIER_SYSTEM,
            },
          },
        ],
        must_not: [approvedExtractionQuery],
      },
    },
  },
});

// Counts documents the same way the extractions list groups them, so the
// badge matches the documents that list shows as needing review.
export const countDocumentsToReview = (
  resources: ExtractedClinicalResource[],
  provenance: ExtractionProvenance[],
) =>
  groupExtractionsBySource(resources, provenance, [], '').filter(
    group => countResourcesToReview(group.resources) > 0,
  ).length;

const searchAuthAtom = atom<ServiceAuth | undefined>(undefined);
const refreshFamily = atomFamily((_key: string) => atom(0));

const countKey = (projectId: string, patientId: string) =>
  `${projectId}\u0000${patientId}`;

const parseCountKey = (key: string) => {
  const [projectId, patientId] = key.split('\u0000');
  return { projectId, patientId };
};

const countsFamily = atomFamily((key: string) => {
  const sourceAtom = abortableAtom(async get => {
    get(refreshFamily(key));
    const auth = get(searchAuthAtom);
    const { projectId, patientId } = parseCountKey(key);
    if (!auth?.account || !auth.authToken || !projectId || !patientId) {
      return undefined;
    }

    const queryOptions = { queryId: 'member-extraction-review-counts' };
    const resources = hitsOf<ExtractedClinicalResource>(
      await fhirSearchService.searchPatient<SearchResponse>(auth)(
        projectId,
        patientId,
        buildMemberExtractionReviewCountQuery(),
        undefined,
        queryOptions,
      ),
    ).filter(
      resource =>
        resource.resourceType &&
        resource.id &&
        !getExtractedResourceReviewDecision(resource),
    );
    if (resources.length === 0) {
      return { documentsToReview: 0 };
    }

    const provenance = hitsOf<ExtractionProvenance>(
      await fhirSearchService.search<SearchResponse>(auth)(
        projectId,
        buildMemberExtractionProvenanceQuery(
          resources.map(resource => `${resource.resourceType}/${resource.id}`),
        ),
        undefined,
        queryOptions,
      ),
    );

    return { documentsToReview: countDocumentsToReview(resources, provenance) };
  });
  const loadableAtom = loadable(sourceAtom);

  return atom(
    (get): CountState => {
      if (!get(searchAuthAtom)?.authToken) {
        return { state: 'idle' };
      }

      const value = get(loadableAtom);
      if (value.state === 'loading') {
        return { state: 'loading' };
      }
      if (value.state === 'hasError') {
        return { state: 'hasError', error: value.error };
      }
      if (value.data === undefined) {
        return { state: 'idle' };
      }
      return { state: 'hasData', data: value.data };
    },
    (get, set) => {
      set(refreshFamily(key), get(refreshFamily(key)) + 1);
    },
  );
});

const idleCountsAtom = atom(
  (): CountState => ({ state: 'idle' }),
  () => undefined,
);

const toResult = (
  result: CountState,
  execute: () => void,
): MemberExtractionReviewCountsResult => ({
  data: result.state === 'hasData' ? result.data : undefined,
  error: result.state === 'hasError' ? result.error : undefined,
  isFetching: result.state === 'loading',
  isIdle: result.state === 'idle',
  execute,
});

export const useMemberExtractionReviewCounts = (
  patientId: MaybeSource<string | null | undefined>,
  options: AsyncOptions = {},
): MemberExtractionReviewCountsResult => {
  const immediate = options.immediate !== false;
  const [started, setStarted] = React.useState(immediate);
  const auth = useServiceAuth();
  const projectId = useActiveProjectId();
  const resolvedPatientId = resolveSource(patientId);
  const setSearchAuth = useSetAtom(searchAuthAtom);

  React.useEffect(() => {
    setSearchAuth(auth);
  }, [auth, setSearchAuth]);

  const canRun = started && Boolean(projectId) && Boolean(resolvedPatientId);
  const [result, refresh] = useAtom(
    canRun
      ? countsFamily(countKey(projectId, resolvedPatientId as string))
      : idleCountsAtom,
  );

  const execute = () => {
    if (!started) {
      setStarted(true);
      return;
    }
    refresh();
  };

  return toResult(result, execute);
};

/** Re-reads the member's review counts wherever they are shown. */
export const useRefreshMemberExtractionReviewCounts = (
  patientId: string | null | undefined,
) => {
  const projectId = useActiveProjectId();
  const bump = useSetAtom(
    refreshFamily(countKey(projectId ?? '', patientId ?? '')),
  );
  return React.useCallback(() => {
    if (projectId && patientId) {
      bump(count => count + 1);
    }
  }, [bump, patientId, projectId]);
};
